/**
 * Draft Editing Service
 * Handles draft document content fetching with caching for the inline editor
 */

import { getDocumentPreview } from "./documentPreviewService";

interface DraftDocument {
  docId: string;
  updatedAt?: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  originalFilename?: string;
}

interface DraftContentResult {
  text: string;
  filePath: string;
  doc: DraftDocument;
  updatedAt?: string;
}

// Module-level cache for draft content
const draftContentCache: Map<string, DraftContentResult> = new Map();

/**
 * Fetch draft content for editing
 */
export const fetchDraftContent = async ({
  projectId,
  doc,
  username
}: {
  projectId: string;
  doc: DraftDocument;
  username: string;
}): Promise<DraftContentResult> => {
  // Check cache first (Phase 4: camelCase only)
  const cacheKey: string = doc.docId;
  if (draftContentCache.has(cacheKey)) {
    return draftContentCache.get(cacheKey)!;
  }

  try {
    // Use existing document preview service to get the content
    const preview = await getDocumentPreview(doc, projectId, username);

    // Convert blob to text
    const text: string = await preview.contentBlob.text();

    const result: DraftContentResult = {
      text,
      filePath: preview.sasData.filePath || '',
      doc,
      updatedAt: doc.updatedAt
    };

    // Cache the result
    draftContentCache.set(cacheKey, result);

    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Clear cached content for a specific draft
 */
export const clearDraftCache = (docId: string): void => {
  if (draftContentCache.has(docId)) {
    draftContentCache.delete(docId);
  }
};

/**
 * Clear all cached draft content
 */
export const clearAllDraftCache = (): void => {
  draftContentCache.clear();
};
